import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useRefetchWhenCatalogChanges } from '../contexts/AdminCatalogContext';
import { putClienteHomeLayoutToApi } from '../lib/clienteHomeLayoutApi';
import {
  CLIENTE_HOME_LAYOUT_STORAGE_KEY,
  defaultClienteHomeLayoutSettings,
  HOME_LAYOUT_UPDATED_EVENT,
  normalizeClienteHomeLayoutSettings,
  type ClienteHomeLayoutSettings,
} from '../../../shared/lib/homeExperiencesLayoutConfig';

function loadStored(): ClienteHomeLayoutSettings {
  try {
    const raw = localStorage.getItem(CLIENTE_HOME_LAYOUT_STORAGE_KEY);
    if (!raw) return defaultClienteHomeLayoutSettings();
    return normalizeClienteHomeLayoutSettings(JSON.parse(raw));
  } catch {
    return defaultClienteHomeLayoutSettings();
  }
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-800">{label}</span>
      {hint ? <span className="block text-xs text-gray-500 mt-0.5">{hint}</span> : null}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function SectionCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <h3 className="text-base font-bold text-gray-900">{title}</h3>
      {subtitle ? <p className="text-sm text-gray-600 mt-1 mb-4">{subtitle}</p> : <div className="mb-4" />}
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export default function DashboardHomeExperiencesLayoutTab() {
  const [draft, setDraft] = useState<ClienteHomeLayoutSettings>(() => loadStored());
  const [savedFlash, setSavedFlash] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useRefetchWhenCatalogChanges(() => {
    setDraft(loadStored());
  });

  const updateLimits = useCallback((patch: Partial<ClienteHomeLayoutSettings['limits']>) => {
    setDraft((d) => ({
      ...d,
      limits: { ...d.limits, ...patch },
    }));
  }, []);

  const updateNoTrip = useCallback((patch: Partial<ClienteHomeLayoutSettings['sectionsNoTrip']>) => {
    setDraft((d) => ({
      ...d,
      sectionsNoTrip: { ...d.sectionsNoTrip, ...patch },
    }));
  }, []);

  const updateComTrip = useCallback((patch: Partial<ClienteHomeLayoutSettings['sectionsComTrip']>) => {
    setDraft((d) => ({
      ...d,
      sectionsComTrip: { ...d.sectionsComTrip, ...patch },
    }));
  }, []);

  const updateStyles = useCallback((patch: Partial<ClienteHomeLayoutSettings['cardStyles']>) => {
    setDraft((d) => ({
      ...d,
      cardStyles: { ...d.cardStyles, ...patch },
    }));
  }, []);

  const normalizedPreview = useMemo(() => normalizeClienteHomeLayoutSettings(draft), [draft]);

  const persistLocalAndNotify = useCallback((next: ClienteHomeLayoutSettings) => {
    localStorage.setItem(CLIENTE_HOME_LAYOUT_STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event(HOME_LAYOUT_UPDATED_EVENT));
  }, []);

  const handleSave = async () => {
    const next = normalizeClienteHomeLayoutSettings(draft);
    setDraft(next);
    setSaveError(null);
    setSaving(true);
    const api = await putClienteHomeLayoutToApi(next);
    setSaving(false);
    if (!api.ok) {
      setSaveError(api.error);
      return;
    }
    persistLocalAndNotify(next);
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 2500);
  };

  const handleReset = async () => {
    const d = defaultClienteHomeLayoutSettings();
    setSaveError(null);
    setSaving(true);
    const api = await putClienteHomeLayoutToApi(d);
    setSaving(false);
    if (!api.ok) {
      setSaveError(api.error);
      return;
    }
    setDraft(d);
    persistLocalAndNotify(d);
  };

  const L = draft.limits;

  return (
    <div className="w-full min-w-0 px-6 pb-10 space-y-6">
      <div className="rounded-lg border border-sky-200 bg-sky-50/90 px-4 py-3 text-sm text-sky-950">
        <p className="font-semibold text-sky-900">Espelho da home «Experiências» do app cliente</p>
        <p className="mt-1 text-sky-900/90">
          A ordem abaixo segue a página real (sem viagem salva). Os limites alimentam os mesmos{' '}
          <code className="text-xs bg-white/80 px-1 rounded">main-container</code> da home. A <strong>fonte de verdade</strong> é a API (
          <code className="text-xs bg-white/80 px-1 rounded">PUT /api/admin/cliente-home-layout</code>, DynamoDB{' '}
          <code className="text-xs bg-white/80 px-1 rounded">DYNAMODB_CLIENTE_HOME_LAYOUT_TABLE</code>
          ). O cliente recebe o mesmo JSON em <code className="text-xs bg-white/80 px-1 rounded">GET /api/catalogo/cliente</code> (campo{' '}
          <code className="text-xs">clienteHomeLayout</code>) e espelha em <code className="text-xs">{CLIENTE_HOME_LAYOUT_STORAGE_KEY}</code>.
          Se a API devolver 500 por tabela em falta, corrija o deploy ou variáveis de ambiente.
        </p>
        <p className="mt-2 text-sky-900/90">
          Também disponível em{' '}
          <Link to="/experiencias/home-cliente" className="font-semibold text-orange-700 hover:underline">
            Experiências → Home (app cliente)
          </Link>
          . Com <code className="text-xs">ADMIN_API_KEY</code> na API, defina <code className="text-xs">VITE_ADMIN_API_KEY</code> no build do admin.
        </p>
      </div>

      {saveError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900" role="alert">
          <p className="font-semibold">Não foi possível gravar na API</p>
          <p className="mt-1">{saveError}</p>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving}
          className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 shadow-sm disabled:opacity-60"
        >
          {saving ? 'A gravar…' : 'Guardar alterações'}
        </button>
        <button
          type="button"
          onClick={() => void handleReset()}
          disabled={saving}
          className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-60"
        >
          Restaurar valores padrão
        </button>
        {savedFlash ? <span className="text-sm font-medium text-green-700">Guardado na API. O cliente hidrata no próximo GET do catálogo.</span> : null}
      </div>

      {/* Ordem visual = home sem viagem */}
      <div className="space-y-4">
        <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500">Sem viagem salva — ordem na home</h2>

        <SectionCard
          title="1 · Destaques"
          subtitle="Carrossel (strip) + link Ver todos. MAIN destaques."
        >
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.sectionsNoTrip.destaques}
              onChange={(e) => updateNoTrip({ destaques: e.target.checked })}
              className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            <span className="text-sm font-medium text-gray-800">Secção visível</span>
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Máx. no pool (Ver todos / feed)" hint="Antes 16.">
              <input
                type="number"
                min={1}
                max={48}
                value={L.destaquesPoolMax}
                onChange={(e) => updateLimits({ destaquesPoolMax: Number(e.target.value) })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Slides no carrossel da home" hint="Antes 8. ≤ pool.">
              <input
                type="number"
                min={1}
                max={24}
                value={L.destaquesCarouselMax}
                onChange={(e) => updateLimits({ destaquesCarouselMax: Number(e.target.value) })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-gray-100">
            <Field label="Estilo do slide" hint="Fill = um cartão por ecrã (atual). Default = largura fixa (~strip).">
              <select
                value={draft.cardStyles.destaquesStripSlide}
                onChange={(e) =>
                  updateStyles({ destaquesStripSlide: e.target.value === 'default' ? 'default' : 'fill' })
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
              >
                <option value="fill">Fill — um destaque por slide (atual)</option>
                <option value="default">Default — cartão estreito (strip clássico)</option>
              </select>
            </Field>
            <Field label="Alinhamento do badge / título" hint="Destaques com título sobre imagem (atual).">
              <select
                value={draft.cardStyles.destaquesBadgeAlign}
                onChange={(e) =>
                  updateStyles({ destaquesBadgeAlign: e.target.value === 'topLeft' ? 'topLeft' : 'withTitle' })
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
              >
                <option value="withTitle">Com título — badge acima do título no bloco inferior (atual)</option>
                <option value="topLeft">Topo esquerdo — badge no canto</option>
              </select>
            </Field>
          </div>
        </SectionCard>

        <SectionCard title="2 · Faça seu Roteiro" subtitle="Banner laranja → Minha programação.">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.sectionsNoTrip.roteiroBanner}
              onChange={(e) => updateNoTrip({ roteiroBanner: e.target.checked })}
              className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            <span className="text-sm font-medium text-gray-800">Banner visível</span>
          </label>
        </SectionCard>

        <SectionCard
          title="3 · Experiências e Lugares"
          subtitle="Pills de categorias + stack intercalado. MAIN experiencias-lugares."
        >
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.sectionsNoTrip.experienciasLugares}
              onChange={(e) => updateNoTrip({ experienciasLugares: e.target.checked })}
              className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            <span className="text-sm font-medium text-gray-800">Secção visível</span>
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Máx. cartões de experiência" hint="Antes 8.">
              <input
                type="number"
                min={0}
                max={32}
                value={L.maxExperienciasExpLugares}
                onChange={(e) => updateLimits({ maxExperienciasExpLugares: Number(e.target.value) })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Máx. cartões de lugar" hint="Antes 8.">
              <input
                type="number"
                min={0}
                max={32}
                value={L.maxLugaresExpLugares}
                onChange={(e) => updateLimits({ maxLugaresExpLugares: Number(e.target.value) })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </Field>
          </div>
        </SectionCard>

        <SectionCard title="4 · Notícias" subtitle="Mais recentes; miniatura ~30% nas primeiras com imagem.">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.sectionsNoTrip.noticias}
              onChange={(e) => updateNoTrip({ noticias: e.target.checked })}
              className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            <span className="text-sm font-medium text-gray-800">Secção visível</span>
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Máx. matérias na lista" hint="Antes 10.">
              <input
                type="number"
                min={1}
                max={30}
                value={L.noticiasSemViagemTotalMax}
                onChange={(e) => updateLimits({ noticiasSemViagemTotalMax: Number(e.target.value) })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Com miniatura (~30%)" hint="Antes 4 primeiras com imagem.">
              <input
                type="number"
                min={0}
                max={20}
                value={L.noticiasSemViagemComImagem}
                onChange={(e) => updateLimits({ noticiasSemViagemComImagem: Number(e.target.value) })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </Field>
          </div>
        </SectionCard>

        <SectionCard title="5 · Eventos" subtitle="Bloco vertical (modo block).">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.sectionsNoTrip.eventos}
              onChange={(e) => updateNoTrip({ eventos: e.target.checked })}
              className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            <span className="text-sm font-medium text-gray-800">Secção visível</span>
          </label>
          <Field label="Máx. cartões" hint="Antes 8.">
            <input
              type="number"
              min={0}
              max={40}
              value={L.maxEventos}
              onChange={(e) => updateLimits({ maxEventos: Number(e.target.value) })}
              className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </Field>
        </SectionCard>

        <SectionCard title="6 · Hospedagem" subtitle="Destaques NaviEX / principal.">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.sectionsNoTrip.hospedagem}
              onChange={(e) => updateNoTrip({ hospedagem: e.target.checked })}
              className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            <span className="text-sm font-medium text-gray-800">Secção visível</span>
          </label>
          <Field label="Máx. cartões" hint="Antes 8.">
            <input
              type="number"
              min={0}
              max={40}
              value={L.maxHospedagens}
              onChange={(e) => updateLimits({ maxHospedagens: Number(e.target.value) })}
              className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </Field>
        </SectionCard>

        <SectionCard
          title="7 · Excursões"
          subtitle="Carrossel alimentado só pelas excursões do menu Excursão (Dynamo). Com viagem: período + destino. Sem viagem: D+1 (GPS se disponível). Ordem por data."
        >
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.sectionsNoTrip.excursões}
              onChange={(e) => updateNoTrip({ excursões: e.target.checked })}
              className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            <span className="text-sm font-medium text-gray-800">Secção visível (quando houver cartões)</span>
          </label>
          <Field label="Slides no carrossel (máx. 10)" hint="Limite de cartões horizontais na home.">
            <input
              type="number"
              min={1}
              max={10}
              value={L.excursõesCarouselMax}
              onChange={(e) => updateLimits({ excursõesCarouselMax: Number(e.target.value) })}
              className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Máx. cartões (legado / compat.)" hint="Mantido para JSON antigo; o carrossel usa «Slides no carrossel» acima.">
            <input
              type="number"
              min={0}
              max={40}
              value={L.maxExcursões}
              onChange={(e) => updateLimits({ maxExcursões: Number(e.target.value) })}
              className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </Field>
        </SectionCard>
      </div>

      <div className="space-y-4 pt-4 border-t border-gray-200">
        <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500">Com viagem salva</h2>

        <SectionCard title="Busca + notícias + extras" subtitle="Ordem na home: busca (opcional) → notícias da cidade → carrossel Destaques (exp./lugares) → eventos → hospedagem → excursões → banner roteiro no fim.">
          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.sectionsComTrip.buscaNoticias}
                onChange={(e) => updateComTrip({ buscaNoticias: e.target.checked })}
                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm font-medium text-gray-800">Campo «Buscar Experiências» (notícias)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.sectionsComTrip.headlinesNoticias}
                onChange={(e) => updateComTrip({ headlinesNoticias: e.target.checked })}
                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm font-medium text-gray-800">Bloco notícias da cidade (mix NaviEX + cidade)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.sectionsComTrip.destaques}
                onChange={(e) => updateComTrip({ destaques: e.target.checked })}
                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm font-medium text-gray-800">Carrossel «Destaques» (experiências e lugares)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.sectionsComTrip.eventos}
                onChange={(e) => updateComTrip({ eventos: e.target.checked })}
                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm font-medium text-gray-800">Eventos</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.sectionsComTrip.hospedagem}
                onChange={(e) => updateComTrip({ hospedagem: e.target.checked })}
                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm font-medium text-gray-800">Hospedagem</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.sectionsComTrip.excursões}
                onChange={(e) => updateComTrip({ excursões: e.target.checked })}
                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm font-medium text-gray-800">Excursões (cidade da viagem)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.sectionsComTrip.roteiroBannerFinal}
                onChange={(e) => updateComTrip({ roteiroBannerFinal: e.target.checked })}
                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm font-medium text-gray-800">Banner «Faça seu Roteiro» no final</span>
            </label>
          </div>

          <div className="pt-4 border-t border-gray-100 grid sm:grid-cols-3 gap-4">
            <Field label="Notícias (total máx.)" hint="pickHomeNewsSectionFeed.">
              <input
                type="number"
                min={1}
                max={30}
                value={L.noticiasComViagemTotalMax}
                onChange={(e) => updateLimits({ noticiasComViagemTotalMax: Number(e.target.value) })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Com miniatura" hint="Antes 4.">
              <input
                type="number"
                min={0}
                max={20}
                value={L.noticiasComViagemComImagem}
                onChange={(e) => updateLimits({ noticiasComViagemComImagem: Number(e.target.value) })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Slots só texto" hint="Antes 6.">
              <input
                type="number"
                min={0}
                max={20}
                value={L.noticiasComViagemTexto}
                onChange={(e) => updateLimits({ noticiasComViagemTexto: Number(e.target.value) })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </Field>
          </div>
          <p className="text-xs text-gray-500">
            Eventos / hospedagem reutilizam os limites da coluna «sem viagem». O carrossel de excursões usa «Slides no carrossel» (máx. 10).
          </p>
        </SectionCard>
      </div>

      <details className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
        <summary className="cursor-pointer font-semibold text-gray-800">Pré-visualização JSON normalizado</summary>
        <pre className="mt-3 text-xs overflow-x-auto whitespace-pre-wrap break-all text-gray-700">
          {JSON.stringify(normalizedPreview, null, 2)}
        </pre>
      </details>
    </div>
  );
}
